import React, { useState } from 'react';
import { LayoutGrid, FileText, Settings as SettingsIcon, Users, ChartBar, Smartphone, Monitor } from 'lucide-react';
import { Button } from '../ui/button';
import {
  AppPageSimple,
  AppPageStack,
  AppPageTabs,
  AppPageSubNav,
  AppPageSubNavTabs,
} from '../orka/layout/AppPage';

/**
 * AppLayoutsShowcase — Design System section: "App Layouts"
 *
 * Displays the five canonical AppPage variants in a previewable, sized
 * frame so the team can see desktop and mobile renderings side-by-side
 * without leaving the design system.
 */

const PreviewFrame = ({ children, mobile, label }) => (
  <div className="space-y-2" data-testid={`layout-preview-${label}`}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold orka-text-tertiary">
      {mobile ? <Smartphone className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
      <span>{label}</span>
    </div>
    <div
      className="rounded-lg border border-border overflow-hidden bg-background"
      style={{
        width: mobile ? 360 : '100%',
        height: 360,
        maxWidth: '100%',
      }}
    >
      {children}
    </div>
  </div>
);

const SECTION_ITEMS = [
  { value: 'overview', label: 'Overview', icon: LayoutGrid },
  { value: 'config',   label: 'Configuration', icon: SettingsIcon },
  { value: 'people',   label: 'People',   icon: Users },
  { value: 'reports',  label: 'Reports',  icon: ChartBar },
  { value: 'docs',     label: 'Docs',     icon: FileText },
];

const TAB_ITEMS = [
  { value: 'all',       label: 'All',       count: 12 },
  { value: 'open',      label: 'Open',      count: 8 },
  { value: 'completed', label: 'Completed', count: 4 },
  { value: 'archived',  label: 'Archived' },
];

// Demo content fillers
const DemoBody = ({ rows = 3 }) => (
  <div className="space-y-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-14 rounded-md bg-muted/40 border border-border/60" />
    ))}
  </div>
);

const DemoActions = () => (
  <>
    <Button size="sm" variant="outline" className="h-7 text-[11px]">Filter</Button>
    <Button size="sm" variant="outline" className="h-7 text-[11px]">Sort</Button>
    <div className="ml-auto" />
    <Button size="sm" className="h-7 text-[11px]">+ New</Button>
  </>
);

const DemoInfo = () => (
  <span>
    <strong className="orka-text-primary">12 items</strong> · last updated 5 minutes ago. Use this strip for context that doesn't change with user actions.
  </span>
);

// ----- Variant previews -----

const SimpleDemo = ({ mobile }) => (
  <AppPageSimple
    title="Simple page"
    eyebrow="01 / Variants"
    subtitle={mobile ? undefined : 'Just a title and a body'}
  >
    <DemoBody />
  </AppPageSimple>
);

const StackDemo = ({ mobile }) => {
  void mobile;
  return (
    <AppPageStack
      title="Stack — info + actions + body"
      eyebrow="02 / Variants"
      subtitle="Top info, action row, then function"
      info={<DemoInfo />}
      actions={<DemoActions />}
    >
      <DemoBody />
    </AppPageStack>
  );
};

const TabsDemo = ({ mobile }) => {
  void mobile;
  const [tab, setTab] = useState('open');
  return (
    <AppPageTabs
      title="Tabs — multi-view module"
      eyebrow="03 / Variants"
      subtitle="Function area driven by tabs"
      actions={<DemoActions />}
      tabs={TAB_ITEMS}
      value={tab}
      onChange={setTab}
    >
      <DemoBody rows={tab === 'open' ? 4 : 2} />
    </AppPageTabs>
  );
};

const SubNavDemo = ({ mobile }) => {
  const [section, setSection] = useState('overview');
  return (
    <AppPageSubNav
      title="SubNav — left side menu"
      eyebrow="04 / Variants"
      subtitle="Inner left submenu, collapsible"
      items={SECTION_ITEMS}
      value={section}
      onChange={setSection}
      defaultCollapsed={mobile}
    >
      <h3 className="text-sm font-semibold mb-3 orka-text-primary">{section}</h3>
      <DemoBody />
    </AppPageSubNav>
  );
};

const SubNavTabsDemo = ({ mobile }) => {
  const [section, setSection] = useState('overview');
  const [tab, setTab] = useState('all');
  return (
    <AppPageSubNavTabs
      title="SubNav + Tabs — full module"
      eyebrow="05 / Variants"
      subtitle="Submenu + actions + tabs + body"
      items={SECTION_ITEMS}
      value={section}
      onChange={setSection}
      tabs={TAB_ITEMS}
      tabValue={tab}
      onTabChange={setTab}
      info={<DemoInfo />}
      actions={<DemoActions />}
      defaultCollapsed={mobile}
    >
      <DemoBody rows={2} />
    </AppPageSubNavTabs>
  );
};

const VARIANT_DEFS = [
  { id: 'simple',       label: '1. Simple',          Demo: SimpleDemo,      bullets: ['Title (+ optional eyebrow / subtitle)', 'Scrollable body', 'Use for: empty states, stub pages, focused readers'] },
  { id: 'stack',        label: '2. Stack',           Demo: StackDemo,       bullets: ['Title → Info → Actions → Body (stacked)', 'Use for: pages with context + controls + content', 'Examples: data lists with filters, dashboards'] },
  { id: 'tabs',         label: '3. Tabs',            Demo: TabsDemo,        bullets: ['Title → Actions → Tab bar → Body', 'Use for: modules with parallel views (Sprints, Kanban, Epic)', 'Tab bar scrolls horizontally on mobile'] },
  { id: 'subnav',       label: '4. SubNav',          Demo: SubNavDemo,      bullets: ['Inner left submenu (collapsible) + Body', 'Use for: settings-like pages with sections (Owner Console)', 'On mobile: submenu becomes a slide-over drawer'] },
  { id: 'subnav-tabs',  label: '5. SubNav + Tabs',   Demo: SubNavTabsDemo,  bullets: ['Submenu + Info + Actions + Tabs + Body', 'Use for: complex modules (Finance with sections, each having tabbed views)', 'The "everything" layout — use sparingly'] },
];

export default function AppLayoutsShowcase() {
  return (
    <div data-testid="ds-section-app-layouts" className="space-y-8">
      <div className="text-xs orka-text-tertiary leading-relaxed">
        Five canonical layouts for an Orka <strong>App Page</strong> — the area inside the global sidebar.
        Each variant bounds itself to the viewport with internal scrolling. Pick the smallest variant that fits.
      </div>

      {VARIANT_DEFS.map((v) => (
        <section key={v.id} data-testid={`app-layouts-variant-${v.id}`} className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold orka-text-primary">{v.label}</h3>
            <ul className="mt-1.5 space-y-0.5">
              {v.bullets.map((b, i) => (
                <li key={i} className="text-[11px] orka-text-tertiary leading-relaxed flex gap-1.5">
                  <span className="orka-text-tertiary">·</span> {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <PreviewFrame label="desktop">
                <v.Demo mobile={false} />
              </PreviewFrame>
            </div>
            <div className="lg:col-span-1">
              <PreviewFrame mobile label="mobile">
                <v.Demo mobile />
              </PreviewFrame>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
